import { useState, useEffect, useContext, createContext, useRef } from "react"
import { ModalKey, ModalContext } from "components/Modals/ModalContext"
import { WhichWeightAPI } from "WhichWeightAPI"
import { useParams } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { useGetExercises } from "redux/customHooks/useGetExercises"
import { initializeTrainingChartData, setSlotDraggingStatus, addDraggedSlot, clearDraggedSlots, resetTrainingChartState, clearProgramTableOverrides } from 'redux/slices/trainingChart'
import { XMarkIcon } from "@heroicons/react/24/outline"
import { IsExerciseSetUnitSeconds } from "functions/exerciseUtils"
import { ff, flags } from 'functions/roleBasedAccessUtils'
import { AppIsInTestEnvironment } from "functions/environmentUtils"

import ScreenFrame from 'components/Frame/ScreenFrame'
import ExerciseProgressionConfig from 'components/TrainingChart/ExerciseProgressionConfig'
import TrainingChart from 'components/TrainingChart/TrainingChart'
import BulkEditorOverrideModal from "components/Modals/Popups/BulkEditorOverrideModal"
import OutlineButton from "components/Buttons/OutlineButton"
import BrandColorButton from "components/Buttons/BrandColorButton"

export const BulkEditViewContext = createContext()

export default function BulkEditView({ propProfileType = 'individual' }) {
    const dispatch = useDispatch()

    const { urlGroupId, urlAthleteId } = useParams()

    const { exercises } = useGetExercises()

    const modalRef = useRef(null)
    const { modalData, ShowModal, HideModal } = useContext(ModalContext)

    const userType = useSelector(state => state.user.type)
    const organizationId = useSelector(state => state.user.organizationId)
    const profileType = useSelector(state => state.trainingChart.profileType)
    const groupId = useSelector(state => state.trainingChart.groupId)
    const athleteId = useSelector(state => state.trainingChart.athleteId)
    const trainingChartPresentDate = useSelector(state => state.trainingChart.trainingChartPresentDate)
    const kinesisEvents = useSelector(state => state.trainingChart.kinesisEvents)
    const latestProgramSnapshot = useSelector(state => state.trainingChart.latestProgramSnapshot)
    const isDragging = useSelector(state => state.trainingChart.draggedSlots.isDragging)
    const draggedSlots = useSelector(state => state.trainingChart.draggedSlots.slots)
    const cachedOverrides = useSelector(state => state.trainingChart.programTableOverrides)

    const [trainingChartDataLoading, setTrainingChartDataLoading] = useState(true)
    const [savingOverrides, setSavingOverrides] = useState(false)

    const canApplyOneOffCoachOverrides = ff(flags.canApplyOneOffCoachOverrides, { profileType: profileType })
    
    // Initialization Functions

    const GetTrainingChartDataFromBackend = async () => {
        if (propProfileType === 'group') {
            GetGroupTrainingChartDataFromBackend()
        }

        if (propProfileType === 'individual') {
            GetInidividualTrainingChartDataFromBackend()
        }
    }

    const GetGroupTrainingChartDataFromBackend = async () => {

        setTrainingChartDataLoading(true)

        dispatch(resetTrainingChartState())

        const jsonBody = {
            groupId: parseInt(urlGroupId),
            localDate: trainingChartPresentDate,
            includeWorkoutTemplateData: 1
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/group_training_chart_data', jsonBody)

        // console.log(result.data.data)

        if (result.data.status === 'failure') {
            const failurePayload = {
                profileType: 'group',
                groupId: parseInt(urlGroupId),
                athleteId: null,
                trainingChartUILocation: 'athlete-profile',
                kinesisEvents: [],
                pastData: [],
                activeData: null,
                latestProgramSnapshot: null
            }

            dispatch(initializeTrainingChartData(failurePayload))

            setTrainingChartDataLoading(false)

            return
        }

        const payload = {
            profileType: 'group',
            groupId: parseInt(urlGroupId),
            athleteId: null,
            trainingChartUILocation: 'athlete-profile',
            ...result.data.data
        }

        dispatch(initializeTrainingChartData(payload))

        setTrainingChartDataLoading(false)
    }

    const GetInidividualTrainingChartDataFromBackend = async () => {

        setTrainingChartDataLoading(true)

        dispatch(resetTrainingChartState())

        const jsonBody = {
            userId: parseInt(urlAthleteId),
            localDate: trainingChartPresentDate,
            includeWorkoutTemplateData: 1
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/training_chart_data', jsonBody)

        // console.log(result.data.data)

        if (result.data.status === 'failure') {
            const failurePayload = {
                profileType: 'individual',
                groupId: null,
                athleteId: parseInt(urlAthleteId),
                trainingChartUILocation: 'athlete-profile',
                kinesisEvents: [],
                pastData: [],
                activeData: null,
                latestProgramSnapshot: null
            }

            dispatch(initializeTrainingChartData(failurePayload))

            setTrainingChartDataLoading(false)

            return
        }

        const payload = {
            profileType: 'individual',
            groupId: null,
            athleteId: parseInt(urlAthleteId),
            trainingChartUILocation: 'athlete-profile',
            ...result.data.data
        }
        
        dispatch(initializeTrainingChartData(payload))

        setTrainingChartDataLoading(false)

    }

    // Dragging Functions

    const OnStartOverrideDrag = (event, dragType, weekOfTheYearKey, workoutTemplateKey, phaseIndex, volumeData, slotIndex, exercise) => {

        if (canApplyOneOffCoachOverrides === false) return;

        if (event.shiftKey) return;

        dispatch(setSlotDraggingStatus(true));

        const draggedSlotPayload = {
            weekOfTheYearKey,
            dragType,
            workoutTemplateKey,
            phaseIndex,
            volumeData,
            slotIndex,
            exercise
        }

        dispatch(addDraggedSlot(draggedSlotPayload));
    }

    const OnOverrideDragEntersCell = (event, dragType, weekOfTheYearKey, workoutTemplateKey, phaseIndex, volumeData, slotIndex, exercise) => {
        if (event.shiftKey) return;

        if (isDragging === false) return;

        // Only allow adding a new slot if it is within the same row as the first dragged slot

        if (
            draggedSlots.length > 0 &&
            draggedSlots[0].dragType === dragType &&
            draggedSlots[0].workoutTemplateKey === workoutTemplateKey &&
            draggedSlots[0].phaseIndex === phaseIndex &&
            draggedSlots[0].slotIndex === slotIndex
        ) {
            const draggedSlotPayload = {
                weekOfTheYearKey,
                dragType,
                workoutTemplateKey,
                phaseIndex,
                volumeData,
                slotIndex,
                exercise
            }

            dispatch(addDraggedSlot(draggedSlotPayload));
        }
    }

    const IsSlotActivelyBeingDragged = (dragType, weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotIndex) => {
        if (dragType === 'phase') {
            return draggedSlots.some(slot =>
                slot.dragType === dragType &&
                slot.weekOfTheYearKey === weekOfTheYearKey &&
                slot.workoutTemplateKey === workoutTemplateKey &&
                slot.phaseIndex === phaseIndex
            );
        }

        if (dragType === 'slot') {
            return draggedSlots.some(slot =>
                slot.dragType === dragType &&
                slot.weekOfTheYearKey === weekOfTheYearKey &&
                slot.workoutTemplateKey === workoutTemplateKey &&
                slot.phaseIndex === phaseIndex &&
                slot.slotIndex === slotIndex
            );
        }
    }

    const OnEndOverrideDrag = () => {

        dispatch(setSlotDraggingStatus(false));

        if (draggedSlots.length > 0) {

            // console.log(draggedSlots)

            const modalData = {
                key: ModalKey.BulkEditorOverride,
                size: 'snapshot-override',
                blurBackground: false,
                athleteId: parseInt(athleteId),
                draggedSlots: JSON.parse(JSON.stringify(draggedSlots)),
                SearchForPhaseOverrideInCache: SearchForPhaseOverrideInCache,
                SearchForSlotOverrideInCache: SearchForSlotOverrideInCache,
                customHideModalAction: () => { 
                    dispatch(clearDraggedSlots()) 
                }
            };

            ShowModal(modalData);

        }

    };

    // Styling Functions

    const GetPhaseCellStyle = (weekOfTheYearKey, workoutTemplateKey, phaseIndex) => {
        if (canApplyOneOffCoachOverrides === false) {
            return 'w-full flex items-center p-2 bg-gray-100 border-b border-gray-400'
        }

        var style = 'w-full flex items-center p-2 border-b border-gray-400 cursor-pointer hover:bg-orange-100'

        if (IsSlotActivelyBeingDragged('phase', weekOfTheYearKey, workoutTemplateKey, phaseIndex, 0)) {
            style += ' bg-orange-100'
            return style
        }

        if (GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-sets-override', phaseIndex).overrideApplied === true) {
            style += ' bg-orange-200'
            return style
        }

        style += ' bg-gray-100'
        return style
    }

    const GetSlotCellStyle = (weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotIndex) => {
        if (canApplyOneOffCoachOverrides === false) {
            return 'h-24 p-2 flex flex-col overflow-y-scroll relative [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
        }

        var style = 'h-24 p-2 flex flex-col overflow-y-scroll relative cursor-pointer hover:bg-orange-100 '
        style += ' [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'

        if (IsSlotActivelyBeingDragged('slot', weekOfTheYearKey, workoutTemplateKey, phaseIndex, slotIndex)) {
            style += ' bg-orange-100'
            return style
        }

        if (
            GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-exercise-swap', phaseIndex, slotIndex).overrideApplied === true
            || GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-reps-override', phaseIndex, slotIndex).overrideApplied === true
            || GetProgramTableDisplayData(weekOfTheYearKey, workoutTemplateKey, 'one-off-weight-recommendation-override', phaseIndex, slotIndex).overrideApplied === true
        ) {
            style += ' bg-orange-200'
            return style
        }

        return style
    }

    const GetCellStyleBasedOnProgressionLevel = (exercise, workoutTemplateKey) => {

        var style = ''

        // Highlight missing exercise cells red
        if (exercise.id === -1) { style += 'bg-red-200 '; return style }

        // Current color scheme
        if (workoutTemplateKey === 'strength-session-a') style += 'bg-blue-200 '
        else if (workoutTemplateKey === 'strength-session-b') style += 'bg-indigo-200 '
        else if (workoutTemplateKey === 'mobility-a') style += 'bg-green-200 '
        else if (workoutTemplateKey === 'mobility-b') style += 'bg-teal-200 '

        // Alternative color scheme 1 - Warmer tones
        // if (workoutTemplateKey === 'strength-session-a') style += 'bg-orange-100 '
        // else if (workoutTemplateKey === 'strength-session-b') style += 'bg-amber-100 '
        // else if (workoutTemplateKey === 'mobility-a') style += 'bg-yellow-100 '
        // else if (workoutTemplateKey === 'mobility-b') style += 'bg-lime-100 '

        // Alternative color scheme 2 - Cooler tones
        // if (workoutTemplateKey === 'strength-session-a') style += 'bg-sky-100 '
        // else if (workoutTemplateKey === 'strength-session-b') style += 'bg-cyan-100 '
        // else if (workoutTemplateKey === 'mobility-a') style += 'bg-emerald-100 '
        // else if (workoutTemplateKey === 'mobility-b') style += 'bg-teal-100 '

        // Alternative color scheme 3 - Pastel tones
        // if (workoutTemplateKey === 'strength-session-a') style += 'bg-pink-100 '
        // else if (workoutTemplateKey === 'strength-session-b') style += 'bg-purple-100 '
        // else if (workoutTemplateKey === 'mobility-a') style += 'bg-blue-100 '
        // else if (workoutTemplateKey === 'mobility-b') style += 'bg-indigo-100 '

        return style

    }

    const injuryData = (workoutTemplateKey, phaseIndex, slotIndex, exercise) => {

        if (exercise.injuryApplied && exercise.injuryMetadata) {
            return {
                injuryMetadata: exercise.injuryMetadata,
                injuryApplied: exercise.injuryApplied
            }
        }

        const slotExerciseProgressionConfig = latestProgramSnapshot.slotExerciseProgressionConfig[workoutTemplateKey]
        const phaseConfig = slotExerciseProgressionConfig.phases[phaseIndex]
        const slotConfig = phaseConfig.slots[slotIndex]

        if (slotConfig.progressionFilters.injuryMetadata && slotConfig.progressionFilters.injuryApplied) {
            return {
                injuryMetadata: slotConfig.progressionFilters.injuryMetadata,
                injuryApplied: slotConfig.progressionFilters.injuryApplied
            }
        } else {
            return null
        }

    }

    // Modal Functions

    const HideOverrideModal = () => {
        dispatch(clearDraggedSlots())
        HideModal()
    }

    const ShowProgressionInfoModal = (workoutTemplateKey, phaseName, phaseIndex, slotIndex) => {

        const modalData = {
            key: ModalKey.SlotExerciseProgressionDetails,
            size: 'fullscreen',
            titleText: 'Slot Exercise Progression Details',
            workoutTemplateKey: workoutTemplateKey,
            phaseName: phaseName,
            phaseIndex: phaseIndex,
            slotIndex: slotIndex
        }

        ShowModal(modalData)

    }

    // Override Functions

    const SearchForPhaseOverrideInCache = (weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex) => {
        const overrideIndex = cachedOverrides.findIndex(o => 
            o.overrideKey === overrideKey 
            && (
                (profileType === 'group' && o.kinesisUserGroupId === parseInt(groupId)) ||
                (profileType === 'individual' && o.userId === parseInt(athleteId))
            ) 
            && o.target.weekOfTheYearKey === weekOfTheYearKey 
            && o.target.workoutTemplateKey === workoutTemplateKey 
            && o.target.phaseIndex === phaseIndex
        )

        if (overrideIndex === -1) {
            return null
        }

        return {
            index: overrideIndex,
            override: cachedOverrides[overrideIndex]
        }
    }

    const SearchForSlotOverrideInCache = (weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex) => {
        const overrideIndex = cachedOverrides.findIndex(o => 
            o.overrideKey === overrideKey 
            && (
                (profileType === 'group' && o.kinesisUserGroupId === parseInt(groupId)) ||
                (profileType === 'individual' && o.userId === parseInt(athleteId))
            ) 
            && o.target.weekOfTheYearKey === weekOfTheYearKey 
            && o.target.workoutTemplateKey === workoutTemplateKey 
            && o.target.phaseIndex === phaseIndex
            && o.target.slotIndex === slotIndex
        )

        if (overrideIndex === -1) {
            return null
        }

        return {
            index: overrideIndex,
            override: cachedOverrides[overrideIndex]
        }
    }

    const GetProgramTableDisplayData = (weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex = 0) => {

        const programTableColumn = latestProgramSnapshot.programTable.find(week => week.weekOfTheYearKey === weekOfTheYearKey)

        switch (overrideKey) {

            case 'one-off-sets-override':
                
                const setsOverride = SearchForPhaseOverrideInCache(weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex)
                
                if (setsOverride !== null) {
                    return {
                        overrideApplied: true,
                        value: setsOverride.override.target.sets
                    }
                }

                return {
                    overrideApplied: false,
                    value: programTableColumn.volumeData.setsAndRepsData[workoutTemplateKey][phaseIndex].sets
                }

            case 'one-off-exercise-swap':

                var databaseExercise = null

                const exerciseOverride = SearchForSlotOverrideInCache(weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex)
                
                if (exerciseOverride !== null) {
                    databaseExercise = exercises.find(e => e.id === exerciseOverride.override.target.exerciseId) || null

                    return {
                        overrideApplied: true,
                        value: databaseExercise
                    }
                }

                const originalExerciseId = programTableColumn.exerciseData[workoutTemplateKey][phaseIndex].slots[slotIndex].id

                databaseExercise = exercises.find(e => e.id === originalExerciseId) || null

                return {
                    overrideApplied: false,
                    value: databaseExercise
                }

            case 'one-off-reps-override':

                // Grab exercise to calculate volume data from. We need this is overriding between setUnits

                var currentExercise = null

                const tempExerciseOverride = SearchForSlotOverrideInCache(weekOfTheYearKey, workoutTemplateKey, 'one-off-exercise-swap', phaseIndex, slotIndex)
                const originalExercise = programTableColumn.exerciseData[workoutTemplateKey][phaseIndex].slots[slotIndex]

                if (tempExerciseOverride !== null) {
                    const tempOverrideExerciseId = tempExerciseOverride.override.target.exerciseId
                    const tempOverrideDatabaseExercise = exercises.find(e => e.id === tempOverrideExerciseId) || null
                    currentExercise = tempOverrideDatabaseExercise
                } else {
                    currentExercise = originalExercise
                }

                // Check for set units override

                const setUnitsOverride = SearchForSlotOverrideInCache(weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex)

                if (setUnitsOverride !== null) {
                    return {
                        overrideApplied: true,
                        value: setUnitsOverride.override.target.reps
                    }
                }

                const slotConfig = latestProgramSnapshot.slotExerciseProgressionConfig[workoutTemplateKey].phases[phaseIndex].slots[slotIndex]
                const phaseVolumeData = programTableColumn.volumeData.setsAndRepsData[workoutTemplateKey][phaseIndex]

                // Get exercise lateral. Default to bilateral if not found.

                var originalExerciseLateral = currentExercise.lateral

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    var tempDatabaseExercise = exercises.find(e => e.id === currentExercise.id) || null
                    originalExerciseLateral = tempDatabaseExercise.lateral
                }

                if (
                    originalExerciseLateral !== 'unilateral' &&
                    originalExerciseLateral !== 'bilateral'
                ) {
                    console.error('Could not find exercise lateral for exercise:', currentExercise.id)
                    originalExerciseLateral = 'bilateral'
                }

                // TODO: Change this
                // Hard coding seconds exercises since getting database exercise for every cell is inefficent
                // We should be caching setUnit

                if (IsExerciseSetUnitSeconds(currentExercise.id) === true) {
                    
                    if (
                        currentExercise.id === 386 // Front Side Flutter
                        || currentExercise.id === 387 // Back Side Flutter
                    ) {
                        return {
                            overrideApplied: false,
                            value: 30
                        }
                    }

                    return {
                        overrideApplied: false,
                        value: 10
                    }
                }

                // TODO: Change this
                // Displaying plyometric volume override in program table
                // We should really be caching category. This method works for now but is inefficent and could miss cells (exercise override for example)

                if (slotConfig?.progressionFilters?.category && slotConfig.progressionFilters.category.includes('plyometric')) {
                    
                    const originalDatabaseExercise = exercises.find(e => e.id === currentExercise.id) || null

                    // console.log(originalDatabaseExercise)

                    if (
                        (originalDatabaseExercise.category === 'plyometric-bilateral' || originalDatabaseExercise.category === 'plyometric-unilateral') &&
                        // Check that the coach has not overridden the reps for this exercise
                        (phaseVolumeData.reps[slotIndex]?.overrideApplied === undefined || phaseVolumeData.reps[slotIndex]?.overrideApplied === false)
                    ) {

                        const plyometricOverrideData = programTableColumn.volumeData.plyometricSetsAndRepsOverrideData

                        // console.log(plyometricOverrideData)

                        return {
                            overrideApplied: false,
                            value: plyometricOverrideData[originalExerciseLateral]
                        }
                    }

                }

                // Slot specific reps data

                if (originalExerciseLateral === 'unilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].unilateral
                    }
                }
        
                if (originalExerciseLateral === 'bilateral') {
                    return {
                        overrideApplied: false,
                        value: phaseVolumeData.reps[slotIndex].bilateral
                    }
                }
        
                return 0

            case 'one-off-weight-recommendation-override': 

                const cachedOverrideDetails = SearchForSlotOverrideInCache(weekOfTheYearKey, workoutTemplateKey, overrideKey, phaseIndex, slotIndex)

                if (cachedOverrideDetails !== null) {
                    return {
                        overrideApplied: true,
                        value: cachedOverrideDetails.override.target.weightRecommendation
                    }
                }

                return {
                    overrideApplied: false,
                    value: 0
                }
        
            default: 
                console.error('Invalid override key:', overrideKey)
                return null
            
        }

    }

    const ClearOverrides = () => {
        dispatch(clearProgramTableOverrides())
    }

    const SaveOverrides = async () => {
        if (!cachedOverrides || cachedOverrides.length === 0) return

        setSavingOverrides(true)

        try {
            if (profileType === 'group') {
                const jsonBody = {
                    groupId: parseInt(groupId),
                    overrides: JSON.stringify(cachedOverrides)
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/one_off_coach_group_override_post', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    return
                }
            }

            if (profileType === 'individual') {
                const jsonBody = {
                    userId: parseInt(athleteId),
                    overrides: JSON.stringify(cachedOverrides)
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/one_off_coach_override_post', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    return
                }
            }
            
            window.location.reload()
            
        } catch (error) {
            console.error('Error saving overrides:', error)
        } finally {
            setSavingOverrides(false)
        }
    }

    // Workout Override Modal Context Data

    const bulkEditViewContextData = {
        userType,
        organizationId,
        ShowProgressionInfoModal,
        OnStartOverrideDrag,
        OnOverrideDragEntersCell,
        OnEndOverrideDrag,
        GetPhaseCellStyle,
        GetSlotCellStyle,
        GetProgramTableDisplayData
    }

    useEffect(() => { 
        GetTrainingChartDataFromBackend() 
    }, [])

    // Handle click-outside for BulkEditorOverride modal
    useEffect(() => {
        if (modalData === null || modalData.key !== ModalKey.BulkEditorOverride) return

        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                HideOverrideModal()
            }
        }

        // Add event listener with a small delay to avoid immediate trigger
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 0)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [modalData, dispatch, HideModal])

    if (exercises.length === 0 || trainingChartDataLoading || profileType === null) {
        return (
            <ScreenFrame limitWidth={false}>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    if (profileType === 'group' && groupId === null) {
        return (
            <ScreenFrame limitWidth={false}>
                <div>Missing group id</div>
            </ScreenFrame>
        )
    }

    if (profileType === 'individual' && athleteId === null) {
        return (
            <ScreenFrame limitWidth={false}>
                <div>Missing athlete id</div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame 
            limitWidth={false}
            onMouseUp={() => OnEndOverrideDrag()}
        >
            <BulkEditViewContext.Provider value={bulkEditViewContextData}>
                {(userType === 'super-admin' || AppIsInTestEnvironment()) && (
                    <ExerciseProgressionConfig exerciseProgressionConfig={latestProgramSnapshot?.slotExerciseProgressionConfig} />
                )}

                <TrainingChart kinesisEvents={kinesisEvents} />

            </BulkEditViewContext.Provider>

            {/* Hacky way of displaying modal while making the page still scrollable */}
            {modalData !== null && modalData.key === ModalKey.BulkEditorOverride && (
                <div className="fixed top-6 right-10 z-50 flex items-center gap-x-2">
                    <div 
                        ref={modalRef}
                        className="relative transform overflow-auto p-4 rounded-lg bg-white shadow-xl border border-gray-400 transition-all w-[420px]"
                    >
                        <div className="flex justify-end">
                            <button 
                                className="rounded-md text-gray-400 hover:text-gray-500" 
                                onClick={() => HideOverrideModal()}
                            >
                                <span className="sr-only">Close</span>

                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>

                        <BulkEditorOverrideModal />
                    </div>
                </div>
            )}

            {cachedOverrides.length > 0 && 
                <div className="fixed bottom-8 right-8 z-50 flex items-center gap-x-2">
                    <div className="w-40 rounded-2xl shadow-lg">
                        <OutlineButton
                            text={`Clear Overrides (${cachedOverrides.length})`}
                            action={ClearOverrides}
                            className="shadow-lg hover:bg-gray-50"
                        />
                    </div>

                    <div className="w-40 rounded-2xl shadow-lg">
                        <BrandColorButton
                            text={`Save Overrides (${cachedOverrides.length})`}
                            action={SaveOverrides}
                            isLoading={savingOverrides}
                            className="shadow-lg"
                        />
                    </div>
                </div>
            }
        </ScreenFrame>
    )
}